import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import WorkSpacesDetails from './workspacesdetails';
import {
	labels,
	workspaceCreationStatus,
	toastDuration,
	toastVariants
} from '../../../../util/uiconstants';
import EllipsesControl from '../../../Common/EllipsesControl/EllipsesControl';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {IconoirPlusCircle} from '@ey-xd/motif-icon';
import MotifButton from '@ey/motif-react-wrapper/Button';
import MotifMenu from '@ey/motif-react-wrapper/Menu';
import AddWorkspaceModal from '../addworkspacemodal';
import Loader from '../../../Common/Loaders/loadingindicator';
import Toast from '../../../Common/Toast/Toast';
import env from '../../../../util/env';
import {getWorkspacesDetails} from '../../../../actions/admin/sharepointworkspacesactions';
import {getLocalizedAPIError} from '../../common/util';
import MotifTooltip from '@ey/motif-react-wrapper/Tooltip';

function WorkspacesWrapper(props) {
	const {
		show,
		onClose,
		currentDatacenter,
		setCurrentDatacenter,
		dataCenterList,
		loadDatacenters,
		userPermissions
	} = props;

	const dispatch = useDispatch();
	const defaultToastMessage = {toastVariant: '', message: ''};

	const [isLoading, setIsLoading] = useState(true);
	const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
	const [toastMessage, setToastMessage] = useState(defaultToastMessage);

	useEffect(() => {
		if (toastMessage.toastVariant === toastVariants.success) {
			let timer = setTimeout(() => {
				setToastMessage(defaultToastMessage);
			}, toastDuration);

			return () => clearTimeout(timer);
		}
	}, [toastMessage.message]);

	useEffect(() => {
		if (currentDatacenter.dataCenterId) {
			loadWorkSpacesDetails();
		}
	}, [currentDatacenter.dataCenterId]);

	const loadWorkSpacesDetails = (showLoading = true) => {
		setIsLoading(showLoading);
		let url =
			env.getURL('serviceUrlV2') +
			`admin/datacenters/${currentDatacenter.dataCenterId}/collaborationsites`;

		dispatch(getWorkspacesDetails(url, currentDatacenter.dataCenterId))
			.then((response) => {
				if (!response.isSDKCommunicationSuccessful) {
					const errorMesage = `${labels.sdkSharePointErrorMessage} ${response.sdkCommunicationErrorMessage}`;
					setToastMessage({
						toastVariant: toastVariants.error,
						message: errorMesage
					});
				}
			})
			.catch((error) => {
				setToastMessage({
					toastVariant: toastVariants.error,
					message: getLocalizedAPIError(error)
				});
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const findCurrentDatacenterIndex = () => {
		const index = dataCenterList.findIndex(
			(element) => element.dataCenterId === currentDatacenter.dataCenterId
		);
		return index;
	};

	const openPreviousDatacenter = () => {
		setIsLoading(true);
		const currentIndex = findCurrentDatacenterIndex();
		setCurrentDatacenter(dataCenterList[currentIndex - 1]);
	};

	const openNextDatacenter = () => {
		setIsLoading(true);
		const currentIndex = findCurrentDatacenterIndex();
		setCurrentDatacenter(dataCenterList[currentIndex + 1]);
	};

	const previousButtonDisabled = () => {
		const currentIndex = findCurrentDatacenterIndex();
		return currentIndex <= 0 ? true : false;
	};

	const nextButtonDisabled = () => {
		const currentIndex = findCurrentDatacenterIndex();
		return currentIndex < 0 || currentIndex === dataCenterList.length - 1
			? true
			: false;
	};

	const onDrawerClose = () => {
		onClose();
	};

	const ellipsesControlRender = (text) => {
		return (
			<section className="roleType">
				<EllipsesControl
					content={text}
					tooltip={text}
					displayId={''}
					noOfLines={1}
					ellipsisClass="columnName"
				/>
			</section>
		);
	};

	const onAddWorkspaceModalClose = (statusObject) => {
		const {dataCenterId, statusId} = statusObject;

		setShowWorkspaceModal(false);
		if (statusId === workspaceCreationStatus.Success) {
			setToastMessage({
				toastVariant: toastVariants.success,
				message: labels.workspaceAddedSuccess
			});
		} else if (statusId === workspaceCreationStatus.Failed) {
			setToastMessage({
				toastVariant: toastVariants.error,
				message: labels.workspaceAddedFailed
			});
		}

		// refresh the workspace list and datacenter list
		if (statusId === workspaceCreationStatus.Success && 
			dataCenterId === currentDatacenter.dataCenterId) {
			loadWorkSpacesDetails(false); // workspace api call to refresh the workspace drawer list
		}
		loadDatacenters(false); // base page api call to refresh the datacenter list
	};

	const hasCreateUpdatePermission = () => {
		return userPermissions?.permissions?.canCreateUpdateCollaborationSites;
	};

	return (
		<MotifMenu
			className={'workspace_menu'}
			show={show}
			onClose={onDrawerClose}
			title={ellipsesControlRender(currentDatacenter.dataCenterName)}
			closeButtonProps={{title: labels.close}}
		>
			{toastMessage.message.length > 0 && (
				<section>
					<Toast
						position="top"
						variant={toastMessage.toastVariant}
						onClose={() => {
							setToastMessage(defaultToastMessage);
						}}
					>
						{toastMessage.message}
					</Toast>
				</section>
			)}

			{isLoading ? (
				<Loader variant={'circular'} />
			) : (
				<>
					<section className="header-wrapper">
						<MotifTooltip
							id={'deletebButtonTooltipId'}
							hideCloseButton
							placement="left"
							flip={false}
							contentClassName="deleteicon"
							hide={hasCreateUpdatePermission() ? true : false}
							trigger={
								<section>
									<MotifButton
										className="add-workspace"
										variant="text-alt"
										size="medium"
										onClick={() => {
											setShowWorkspaceModal(true);
										}}
										disabled={hasCreateUpdatePermission() ? false : true}
									>
										<MotifIcon
											className="icon-container"
											iconFunction={IconoirPlusCircle}
											size="20"
										/>
										{labels.addWorkspace}
									</MotifButton>
								</section>
							}
						>
							{labels.insufficientPermission}
						</MotifTooltip>
					</section>

					{currentDatacenter.dataCenterId && <WorkSpacesDetails />}

					<section className="footer_button">
						<MotifButton
							className="previousBtn"
							variant="secondary"
							onClick={openPreviousDatacenter}
							disabled={previousButtonDisabled()}
							stepper="prev"
							size="medium"
						>
							{labels.previous}
						</MotifButton>

						<MotifButton
							variant="primary"
							onClick={openNextDatacenter}
							disabled={nextButtonDisabled()}
							stepper="next"
							size="medium"
						>
							{labels.next}
						</MotifButton>
					</section>
				</>
			)}
			<section>
				{showWorkspaceModal && (
					<AddWorkspaceModal
						showModal={showWorkspaceModal}
						defaultDatacenterId={currentDatacenter.dataCenterId}
						onClose={onAddWorkspaceModalClose}
					/>
				)}
			</section>
		</MotifMenu>
	);
}

export default WorkspacesWrapper;
